import { Component } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';

import classNames from 'clsx';
import interpolateComponents from 'interpolate-components';
import Input from '../shared/form/Input';
import PillButton from '../shared/button/PillButton';
import withCreditCardHelper from '../../decorators/subscription/withCreditCardHelper';

import getRequiredLabel from '../../utils/form/getRequiredLabel';
import {
  SUBSCRIBE_PAYMENT_FORM_BILLING_FORM,
  SUBSCRIBE_PAYMENT_FORM_ADDRESS_TWO,
  SUBSCRIBE_PAYMENT_FORM_BILLING_ZIPCODE,
  SUBSCRIBE_PAYMENT_FORM_CITY,
  SUBSCRIBE_PAYMENT_FORM_FIRST_NAME,
  SUBSCRIBE_PAYMENT_FORM_LAST_NAME,
  SUBSCRIBE_GO_PREMIUM_NOW_LABEL,
  SUBSCRIBE_SUBMIT_BUTTON,
  SUBSCRIBE_TERMS_AGREEMENT,
  SUBSCRIBE_TERMS_AGREEMENT_ERROR,
} from '../../constants/localizations/subscription';

import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import CheckBox from '../shared/form/CheckBox';
import getOverrideOrDefaultResourceKey from '../../utils/getOverrideOrDefaultResourceKey';
import generateOriginLabelFromQuery from '../../utils/generateOriginLabelFromQuery';
import { SOURCE_ONAIR } from '../../constants/sourceQueryParams';
import UnderlineLink from '../shared/link/UnderlineLink';
import partnerAuthSharedStyles, {
  inputMappedPartnerAuthStyles,
} from '../shared/styles/partnerAuthSharedStyles';
import LocationField from './fields/LocationField';
import CountryStateField from './fields/CountryStateField';
import ErrorMessages from './ErrorMessages';
import css from './payment.module.scss';

const hintStyle = { fontSize: '16px' };

/**
 * The CreditCardForm takes both a custom onSubmit and the redux-form
 * handleSubmit function. This component is responsible for calling the
 * onSubmit handler when the form is submitted
 */
class CreditCardForm extends Component {
  static propTypes = {
    // from parent
    selectedProduct: PropTypes.object,
    form: PropTypes.object.isRequired,
    formId: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onSubmitFailure: PropTypes.func.isRequired,
    onSubmitSuccess: PropTypes.func.isRequired,
    disableCreditCardForm: PropTypes.func.isRequired,
    freeTrialEnabled: PropTypes.bool.isRequired,
    hasSetupFee: PropTypes.bool.isRequired,
    actions: PropTypes.object.isRequired,
    defaultSubmitButtonLabel: PropTypes.string.isRequired,
    paymentFlowLayoutProps: PropTypes.object.isRequired,
    className: PropTypes.string,
    isFormDisabled: PropTypes.bool,
    // from withCreditCardHelper
    locationOptions: PropTypes.array,
    enableManualSelection: PropTypes.bool,
    setFormInstance: PropTypes.func.isRequired,
    generateRecurlyToken: PropTypes.func.isRequired,
    recurlyCardFields: PropTypes.object.isRequired,
    recurlyHiddenFields: PropTypes.object.isRequired,
    // from redux-form
    handleSubmit: PropTypes.func.isRequired,
    error: PropTypes.object,
    submitting: PropTypes.bool,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);
    this.manageSubmission = this.manageSubmission.bind(this);
  }

  /**
   * The manageSubmission function that is passed to the payment form.
   *
   * The manageSubmission form handler is called with the form data that is registered
   * with redux-form via the Field components.
   *
   * The manageSubmission steps:
   *
   *  1) Calls updateLocationPaymentInformation. The updateLocationPaymentInformation action
   *  creator tries to verify the country and state of the submitted postalCode via an API
   *  call to `/api/v1/location`. If this call returns a country and state, the action creator
   *  updates the store with the returned country and state, and then resolves.
   *
   *  If the location API call does not return a valid country and state, then the action creator
   *  sets an enableManualSelection flag to true in the store, and throws an error to cancel the
   *  form submission.
   *
   *  2) Calls the onSubmit method with the selectedProduct.
   *
   * @param data {object} - postalCode (required), country, state
   * @returns {Promise}
   */
  async manageSubmission(data = {}) {
    // disable the form when we start submission processing
    this.props.disableCreditCardForm();

    const { getLocalizedText, location } = this.context;
    const source = generateOriginLabelFromQuery(location);
    const isOnAirFlow = source === SOURCE_ONAIR;
    const { onAirTermsCheckbox } = data;

    // TuneIn On Air flow enforces agreement to our broadcaster terms
    if (isOnAirFlow && !onAirTermsCheckbox) {
      this.props.onSubmitFailure(
        { message: SUBSCRIBE_TERMS_AGREEMENT_ERROR },
        getLocalizedText(SUBSCRIBE_TERMS_AGREEMENT_ERROR),
      );
      return;
    }

    try {
      const { postalCode, country, state } = data;
      await this.props.actions.updateLocationPaymentInformation(
        postalCode,
        country,
        state,
      );

      // generate the recurly token needed to create an account
      const token = await this.props.generateRecurlyToken();

      // Call the props onSubmit handler. The 'await' keyword implicitly wraps
      // result in Promise.resolve if the return value of onSubmit is not
      // a Promise.
      await this.props.onSubmit(this.props.selectedProduct, token);

      this.props.onSubmitSuccess();
    } catch (error) {
      this.props.onSubmitFailure(error);
    }
  }

  render() {
    const {
      form,
      formId,
      className,
      paymentFlowLayoutProps,
      handleSubmit,
      setFormInstance,
      isFormDisabled,
      locationOptions,
      enableManualSelection,
      error,
      actions,
      submitting,
      defaultSubmitButtonLabel,
      recurlyCardFields,
      recurlyHiddenFields,
      freeTrialEnabled,
      hasSetupFee,
    } = this.props;
    const { getLocalizedText, location } = this.context;
    const { isPairingFlow } = paymentFlowLayoutProps || {};

    const submitButtonLabel = getOverrideOrDefaultResourceKey(
      getLocalizedText,
      location,
      defaultSubmitButtonLabel.match(SUBSCRIBE_GO_PREMIUM_NOW_LABEL) &&
        !hasSetupFee &&
        freeTrialEnabled
        ? SUBSCRIBE_SUBMIT_BUTTON
        : defaultSubmitButtonLabel,
    );

    const source = generateOriginLabelFromQuery(location);
    const isOnAirFlow = source === SOURCE_ONAIR;
    const agreementText = interpolateComponents({
      mixedString: getLocalizedText(SUBSCRIBE_TERMS_AGREEMENT),
      components: {
        a1: (
          <UnderlineLink
            data-testid="policyLink"
            className={classNames(css.legalLink, css.legalLinkCheckbox)}
            to="https://tunein.com/policies/on-air-addendum"
            target="_blank"
            isNotReactRouterLink
            isDropDown
          />
        ),
      },
    });

    const baseFieldProps = {
      className: css.inputField,
      component: Input,
      inlineErrors: true,
      disabled: isFormDisabled,
    };
    const dynamicFieldProps = isPairingFlow
      ? {
          ...inputMappedPartnerAuthStyles,
          floatingLabel: true,
          finalStyleOverride: {
            ...partnerAuthSharedStyles.textFieldBackgroundStyle,
          },
        }
      : {
          hintStyle,
        };

    return (
      <form
        data-testid={`${formId}PaymentForm`}
        onSubmit={handleSubmit(this.manageSubmission)}
        ref={setFormInstance} // RCF
        className={className}
      >
        <div
          className={classNames(css.innerPaymentFields, {
            [css.isPairingFlow]: isPairingFlow,
          })}
        >
          <div
            className={classNames(css.inlineFieldsContainer, {
              [css.isPairingFlow]: isPairingFlow,
            })}
          >
            <div
              className={classNames(
                css.inlineField,
                {
                  [css.isPairingFlow]: isPairingFlow,
                },
                css.paymentFieldSection,
              )}
            >
              <div
                data-testid="firstNameContainer"
                className={classNames({ [css.leftField]: !isPairingFlow })}
              >
                <Field
                  {...baseFieldProps}
                  {...dynamicFieldProps}
                  tabIndex={1}
                  id={`${formId}FirstName`}
                  data-testid="firstName"
                  name="firstName"
                  label={getRequiredLabel(
                    getLocalizedText(SUBSCRIBE_PAYMENT_FORM_FIRST_NAME),
                  )}
                />
              </div>
            </div>
            <div
              className={classNames(
                css.inlineField,
                {
                  [css.isPairingFlow]: isPairingFlow,
                },
                css.paymentFieldSection,
              )}
            >
              <div
                data-testid="lastNameContainer"
                className={classNames({ [css.rightField]: !isPairingFlow })}
              >
                <Field
                  {...baseFieldProps}
                  {...dynamicFieldProps}
                  tabIndex={2}
                  id={`${formId}LastName`}
                  data-testid="lastName"
                  name="lastName"
                  label={getRequiredLabel(
                    getLocalizedText(SUBSCRIBE_PAYMENT_FORM_LAST_NAME),
                  )}
                />
              </div>
            </div>
          </div>
          {recurlyCardFields}
          <div
            className={classNames('row', css.paymentFieldSection, {
              [css.isPairingFlow]: isPairingFlow,
            })}
          >
            <div data-testid="addressOneContainer" className="col-xs-12">
              <Field
                {...baseFieldProps}
                {...dynamicFieldProps}
                tabIndex={7}
                id={`${formId}AddressOne`}
                data-testid="addressOne"
                name="addressOne"
                label={getRequiredLabel(
                  getLocalizedText(SUBSCRIBE_PAYMENT_FORM_BILLING_FORM),
                )}
              />
            </div>
          </div>
          <div
            className={classNames('row', css.paymentFieldSection, {
              [css.isPairingFlow]: isPairingFlow,
            })}
          >
            <div data-testid="addressTwoContainer" className="col-xs-12">
              <Field
                {...baseFieldProps}
                {...dynamicFieldProps}
                tabIndex={8}
                id={`${formId}AddressTwo`}
                data-testid="addressTwo"
                name="addressTwo"
                label={getLocalizedText(SUBSCRIBE_PAYMENT_FORM_ADDRESS_TWO)}
              />
            </div>
          </div>
          <div
            className={classNames(css.inlineFieldsContainer, {
              [css.isPairingFlow]: isPairingFlow,
            })}
          >
            <div
              className={classNames(
                css.inlineField,
                {
                  [css.isPairingFlow]: isPairingFlow,
                },
                css.paymentFieldSection,
              )}
            >
              <div
                data-testid="cityContainer"
                className={classNames({ [css.leftField]: !isPairingFlow })}
              >
                <Field
                  {...baseFieldProps}
                  {...dynamicFieldProps}
                  tabIndex={9}
                  id={`${formId}City`}
                  data-testid="city"
                  name="city"
                  label={getRequiredLabel(
                    getLocalizedText(SUBSCRIBE_PAYMENT_FORM_CITY),
                  )}
                />
              </div>
            </div>
            <div
              className={classNames(
                css.inlineField,
                {
                  [css.isPairingFlow]: isPairingFlow,
                },
                css.paymentFieldSection,
              )}
            >
              <div
                data-testid="postalCodeContainer"
                className={classNames({ [css.rightField]: !isPairingFlow })}
              >
                <Field
                  {...baseFieldProps}
                  {...dynamicFieldProps}
                  tabIndex={10}
                  id={`${formId}PostalCode`}
                  data-testid="postalCode"
                  name="postalCode"
                  label={getRequiredLabel(
                    getLocalizedText(SUBSCRIBE_PAYMENT_FORM_BILLING_ZIPCODE),
                  )}
                />
              </div>
            </div>
          </div>
          <LocationField
            form={form}
            formId={formId}
            locationOptions={locationOptions}
            selectLocationOption={actions.selectLocationOption}
            disableAll={isFormDisabled}
          />
          <CountryStateField
            enableManualSelection={enableManualSelection}
            isPairingFlow={isPairingFlow}
            form={form}
          />
          {isOnAirFlow && (
            <Field
              component={CheckBox}
              customSpanClass={css.onAirTermsCheckboxContainer}
              customCheckmarkClass={css.onAirTermsCheckbox}
              labelClassName={css.onAirTermsLabel}
              key="onAirTermsCheckbox"
              dataTestId="onAirTermsCheckbox"
              id="onAirTermsCheckbox"
              name="onAirTermsCheckbox"
              label={agreementText}
              isBold
            />
          )}
        </div>
        <div className={css.errorContainer}>
          <ErrorMessages error={error} />
        </div>
        <div className="row">
          <div className={classNames('col-xs-12')}>
            <PillButton
              id={`${formId}SubmitButton`}
              isDisabled={submitting || isFormDisabled}
              type="submit"
              dataTestId={`${formId}SubmitButton`}
              label={getLocalizedText(submitButtonLabel)}
              className={css.button}
              tabIndex="14"
            />
          </div>
        </div>
        {recurlyHiddenFields}
      </form>
    );
  }
}

export default withCreditCardHelper(CreditCardForm);
